/*
  The API client.

  Everything is cookie-authenticated and same-origin: `credentials: 'include'` on every
  call, and in dev Vite proxies /api and /auth to the API so the cookie is actually set
  rather than dropped as cross-site.

  A 401 is not an error to display. It means the session ended, and the shell reacts by
  showing the sign-in screen, so it gets its own signal rather than a thrown message the
  user would have to read.
*/

export class Unauthorized extends Error {
  constructor() {
    super('not signed in')
    this.name = 'Unauthorized'
  }
}

/** The providers the server will accept, matching SocialProviders.TryParse. */
export type SocialProviderName = 'google' | 'apple' | 'facebook'

export class ApiError extends Error {
  // Written out rather than a parameter property: the template enables
  // erasableSyntaxOnly, which forbids syntax that emits runtime code from a type position.
  readonly status: number

  /**
   * The parsed response body, where there was one.
   *
   * Most refusals carry only { error } and the message is the whole story. Social sign-in
   * is the exception: "no account for this Google address yet" is a step in a flow rather
   * than a failure, and the fields that drive that step travel in the body.
   */
  readonly body: unknown

  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/*
  Where the API is.

  Empty string on the web, where the SPA is served out of the API's own wwwroot and every
  path is already same-origin. The iOS build sets VITE_API_BASE to https://www.boardium.co,
  because a Capacitor app is served from capacitor://localhost and a bare /api/... would
  resolve against that and 404 against the bundle.

  The session cookie survives that boundary because CapacitorHttp routes fetch through the
  native URLSession, which keeps its own cookie store and is not a browser: CORS and
  SameSite never come into it. Turn CapacitorHttp off in capacitor.config.ts and every
  request here starts arriving unauthenticated, which looks like a broken login and is not.
*/
const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')

/**
 * An absolute URL for a server path, for the handful of places that call fetch directly
 * because they are streaming a file rather than exchanging JSON.
 *
 * Only rewrites paths that start with a slash. Anything already absolute is somebody else's
 * server and none of this applies to it.
 */
export function apiUrl(path: string): string {
  return path.startsWith('/') ? API_BASE + path : path
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(apiUrl(path), {
    ...init,
    credentials: 'include',
    headers: {
      // Only where the body is already serialized. A FormData body must go out with no
      // content type at all so the browser can set its own multipart boundary; naming it
      // JSON here produced a request the server could not read as a form.
      ...(typeof init?.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  if (response.status === 401) throw new Unauthorized()

  if (!response.ok) {
    /*
      The API returns { error } for anything it rejects deliberately.

      Three things can leave that empty and all three happened at once on the trial button.
      ASP.NET's own Results.Problem sends ProblemDetails, which carries `detail` and `title`
      and no `error` at all. An unhandled exception sends a 500 with no body to parse. And
      HTTP/2 dropped the reason phrase, so `statusText` is the empty string on this site
      rather than "Internal Server Error".

      The result was an ApiError whose message was "", which every caller then rendered
      with `{error && ...}`. An empty string is falsy, so the UI drew nothing and the
      button read as dead. Never return an empty message from here.
    */
    let body: unknown
    let message = ''
    try {
      body = await response.json()
      const b = body as { error?: string; detail?: string; title?: string }
      message = b?.error || b?.detail || b?.title || ''
    } catch {
      /* not JSON; the fallbacks below cover it */
    }
    if (!message) message = response.statusText || `Request failed (${response.status})`
    throw new ApiError(response.status, message, body)
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const api = {
  get:  <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  postForm: <T>(path: string, body: FormData) =>
    request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  // The body is optional and almost always absent. Closing an account is the exception:
  // it carries the typed confirmation, and inventing a POST /delete route to avoid a DELETE
  // with a body would be naming the verb twice.
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
}

// ─── Shapes the API actually returns ────────────────────────────────────────

export interface Account {
  id: string
  email: string
  displayName: string | null
  role: string
  businessId: string
  hasGoogle: boolean
  /** The business's trade, free text the owner typed. Null on an account with none set. */
  trade: string | null
  /** The business's name. Home opens with it. */
  company: string | null
  /**
   * When this business was walked through the setup run, or null if it never has been.
   * Null opens Onboarding instead of the shell. Which steps are done is worked out from
   * the profile, the board and the figures, never from this.
   */
  onboardedAt: string | null
  /** False until the address has been confirmed from an emailed link. */
  emailVerified: boolean
}

export type ChipState = 'empty' | 'filling' | 'prepped' | 'curating' | 'running'

/*
  Home's metric read. Declared here rather than inside the card that used to be the only
  thing rendering it: the masthead strip and the anomaly card are now two readers of one
  fetch, and a shape owned by one of them would have to be imported by the other.
*/
export interface MetricTile {
  key: string
  label: string
  value: number
  unit: string
  delta: number | null
  deltaPercent: number | null
  higherIsBetter: boolean
}

export interface HomeMetrics {
  tiles: MetricTile[]
  connected: boolean
  updatedAt: string | null
  anomaly: {
    id: string
    metric: string
    label: string
    severity: string
    narrative: string | null
    flaggedBy: string | null
    flaggedFace: string | null
    flaggedRamp: string | null
  } | null
}

export interface NextMeeting {
  meetingId: string | null
  scheduledFor: string | null
  chipState: ChipState
  itemCount: number
  preppedCount: number
  awaitingAnswerCount: number
  overdueCommitmentCount: number
  willUseFallback: boolean
}

export interface AgendaItem {
  id: string
  businessId: string
  rawText: string
  source: 'manual' | 'escalation' | 'system' | 'email'
  clarifierQuestion: string | null
  clarifierAnswer: string | null
  clarifierMemberId: string | null
  clarifierAskedAt: string | null
  clarifierAnsweredAt: string | null
  sourceConsultId: string | null
  consultAnswer: string | null
  consultAskedAt: string | null
  consultMemberId: string | null
  consultAnsweredAt: string | null
  clusterId: string | null
  clusterLabel: string | null
  prepPayload: string | null
  status: string
  deferredCount: number
  meetingId: string | null
  displayOrder: number | null
  submittedAt: string
}

export interface BoardMember {
  id: string
  displayName: string
  seatRole: 'member' | 'chair' | 'dissenter'
  domains: string[]
  attributes: PersonaAttributes
  backstory: string | null
  archetype: string
  colorRamp: string
  avatarUrl: string | null
  modelTier: 'economy' | 'standard' | 'principal'
  isCustom: boolean
  seatOrder: number

  /*
    The one word this seat would be introduced by, read off its strongest competence.
    Computed by the server rather than here: which attribute counts as a seat's discipline
    is product judgement, and a second copy of the rule in TypeScript would drift from the
    one that routes the question.
  */
  discipline: string
}

/*
  Something a member asked the owner to do, offered under the answer it was said in. A
  suggestion until accepted: acceptedAt and commitmentId are null while it is still just
  something that was said.
*/
export interface ConsultActionItem {
  id: string
  consultId: string
  messageSeq: number
  boardMemberId: string | null
  text: string
  dueInDays: number
  successMetric: string
  commitmentId: string | null
  acceptedAt: string | null
  createdAt: string
}

/*
  The 19-attribute vector (PERSONA SPEC §1). Deliberately loose maps rather than 19 named
  fields: the definitions are served from the database so band wording can be tuned without
  a deploy, and a hardcoded interface here would be a second source of truth that silently
  drifts from the first.
*/
export interface PersonaAttributes {
  disposition: Record<string, number>
  competence: Record<string, number>
  stature: Record<string, number>
}

export type AttributeGroup = 'disposition' | 'competence' | 'stature'

export interface AttributeBand {
  directive: string | null
  readout: string
}

export interface AttributeDefinition {
  key: string
  category: AttributeGroup
  displayName: string
  poleLow: string
  poleHigh: string
  budgeted: boolean
  sortOrder: number
  bands: Record<string, AttributeBand>
}

export interface AttributeCatalog {
  competenceBudget: number
  definitions: AttributeDefinition[]
}

export interface BoardFinding {
  check: string
  severity: 'warning' | 'blocking'
  message: string
  isBlocking: boolean
}

export interface BoardValidation {
  findings: BoardFinding[]
  blocking: number
  warnings: number
}

export interface PersonaPreset {
  key: string
  displayName: string
  archetype: string
  backstory: string | null
  attributes: PersonaAttributes
  domains: string[]
  avatarUrl: string | null
}

export interface SeatUpdate {
  displayName: string
  attributes: PersonaAttributes
  domains: string[]
  archetype: string | null
  backstory: string | null
  customPrompt: string | null
  avatarUrl: string | null
}

/*
  Band boundaries from §2.1, duplicated here only because the slider has to pick a readout
  on every drag and a round trip per pixel is not a thing. The thresholds are the one part
  of the model that is structural rather than tunable; the wording they select still comes
  from the server.
*/
export function bandFor(value: number): string {
  if (value <= 1) return 'very_low'
  if (value <= 4) return 'low'
  if (value <= 6) return 'mid'
  if (value <= 8) return 'high'
  return 'very_high'
}

export function readoutFor(definition: AttributeDefinition, value: number): string {
  return definition.bands[bandFor(value)]?.readout ?? ''
}

export function competenceTotal(attributes: PersonaAttributes): number {
  return Object.values(attributes.competence).reduce((a, b) => a + b, 0)
}

export type Priority = 'high' | 'normal' | 'low'

/** Lower sorts first. Mirrors CommitmentPriority.Rank so the screen and the server agree. */
export const priorityRank = (p: Priority): number => (p === 'high' ? 0 : p === 'low' ? 2 : 1)

export interface Commitment {
  id: string
  meetingId: string | null
  text: string
  owner: string | null
  dueDate: string | null
  successMetric: string
  status: string
  /** How much it matters: set by the board, changeable by the owner. */
  priority: Priority
  supersededByPushback: boolean

  /*
    Who will put this back on the table, and when. Predicted server-side with the same
    function the nightly sweep uses, so the name on the card is the name that turns up.
    Null when the commitment is not due before the next meeting.
  */
  followUpBy: string | null
  followUpFace: string | null
  followUpOn: string | null

  /** What the owner has already handed over against this. Newest last. */
  answers: CommitmentAnswer[]

  /*
    When the board asked for it. An instant, not a calendar day, so it is compared as a
    string rather than parsed through src/dates.ts: ISO-8601 in UTC sorts lexically, which
    is all the newest and oldest orderings on Actions need of it.

    The endpoint has always returned this. The interface simply never declared it, so the
    first thing to read it did not compile.
  */
  createdAt: string
}

export interface CommitmentAnswer {
  id: string
  note: string | null
  documentId: string | null
  fileName: string | null
  createdAt: string
  /** True once the attached file has been read, so the card can say whether the board can use it. */
  readable: boolean
}

export interface Consult {
  id: string
  question: string
  answer: string | null
  status: 'pending' | 'answered' | 'failed'
  error: string | null
  membersUsed: string[] | null
  escalatedItemId: string | null
  createdAt: string
}

export interface ConsultMessage {
  id: string
  seq: number
  role: 'owner' | 'member'
  boardMemberId: string | null
  body: string
  createdAt: string
}

export interface ConsultThread {
  messages: ConsultMessage[]
  turnsLeft: number
  canReply: boolean
  closed: boolean
  escalatedItemId: string | null
  status: string
}

export interface Meeting {
  id: string
  status: string
  isFallback: boolean
  scheduledFor: string | null
  completedAt: string | null
  /** The first line of what the board decided. Null until it has met. */
  headline: string | null
  itemCount: number

  /** How the vote went. All three are 0 until the meeting has decided. */
  supportVotes: number
  opposeVotes: number
  conditionalVotes: number
}

export const endpoints = {
  me:           () => api.get<Account>('/auth/me'),
  login:        (email: string, password: string) => api.post<Account>('/auth/login', { email, password }),
  logout:       () => api.post<void>('/auth/logout'),

  signup:       (body: {
                  email: string; password: string; company: string
                  trade: string; name?: string; timezone?: string
                }) => api.post<Account>('/auth/signup', body),

  /**
   * Signs in with a provider token. Throws ApiError 404 with { needsAccount: true } when
   * the token is good and there is simply no account behind it yet, which is the signal to
   * ask for a business name and a trade.
   */
  /*
    `idToken` carries a signed assertion for Google and Apple and an opaque access token for
    Facebook. The name is Google's and Apple's; renaming it would touch the wire, both
    endpoints and every call site to describe the same field. What the server does with it
    is per provider anyway.
  */
  socialLogin:  (provider: SocialProviderName, idToken: string) =>
                  api.post<Account>('/auth/social', { provider, idToken }),

  socialSignup: (body: {
                  provider: SocialProviderName; idToken: string
                  company: string; trade: string; timezone?: string
                }) => api.post<Account>('/auth/social/signup', body),

  changePassword: (currentPassword: string, newPassword: string) =>
                  api.post<void>('/auth/password', { currentPassword, newPassword }),

  // Always resolves the same way whether the address is registered or not, so the caller
  // has nothing to leak either.
  forgot:       (email: string) =>
                  api.post<{ sent: boolean; message: string }>('/auth/forgot', { email }),

  resetPassword: (token: string, password: string) =>
                  api.post<Account>('/auth/reset', { token, password }),

  verifyEmail:  (token: string) => api.post<Account>('/auth/verify', { token }),

  // Reads an invitation without spending it, so the join screen can name the company first.
  peekInvite:   (token: string) =>
                  api.get<{ email: string; company: string }>(`/auth/invite?token=${encodeURIComponent(token)}`),

  join:         (token: string, password: string, name?: string) =>
                  api.post<Account>('/auth/join', { token, password, name }),

  // Same always-identical response as forgot(), so it leaks nothing either.
  resendVerification: (email: string) =>
                  api.post<{ sent: boolean; message: string }>('/auth/resend', { email }),

  nextMeeting:  () => api.get<NextMeeting>('/api/next-meeting'),
  inbox:        () => api.get<AgendaItem[]>('/api/agenda'),
  capture:      (text: string, documentIds?: string[]) =>
                  api.post<{ id: string }>('/api/agenda', { text, documentIds: documentIds ?? null }),
  answer:       (id: string, answer: string) => api.post<void>(`/api/agenda/${id}/clarifier`, { answer }),
  patchItem:    (id: string, action: 'defer' | 'delete' | 'restore') =>
                  api.patch<void>(`/api/agenda/${id}`, { action }),

  board:        () => api.get<BoardMember[]>('/api/board'),
  attributes:   () => api.get<AttributeCatalog>('/api/board/attributes'),
  presets:      () => api.get<PersonaPreset[]>('/api/board/presets'),
  validation:   () => api.get<BoardValidation>('/api/board/validation'),

  homeMetrics:  () => api.get<HomeMetrics>('/api/home/metrics'),
  activity:     () => api.get<never>('/api/home/activity'),
  seasonal:     () => api.get<never>('/api/home/seasonal'),
  lastDecision: () => api.get<never>('/api/home/last-decision'),
  saveSeat:     (id: string, seat: SeatUpdate) => api.put<void>(`/api/board/members/${id}`, seat),
  commitments:  () => api.get<Commitment[]>('/api/commitments'),
  resolve:      (id: string, status: string, outcomeNote?: string) =>
                  api.post<void>(`/api/commitments/${id}/resolve`, { status, outcomeNote }),

  meetings:     () => api.get<Meeting[]>('/api/meetings'),
  minutes:      (id: string) => api.get<unknown>(`/api/meetings/${id}/minutes`),

  // memberId is the seat the owner picked by hand. Omitted, the chair takes it and brings
  // in the discipline owner when the question sits squarely on one ground.
  //
  // documentIds are files uploaded a moment ago and not yet attached to anything. They are
  // sent with the question rather than posted afterwards on purpose: the worker waits for
  // attachments it can see, and one linked after the job started is one it never knew to
  // wait for.
  ask:          (question: string, memberId?: string | null, documentIds?: string[]) =>
    api.post<{ id: string; status: string }>('/api/consults', {
      question, memberId: memberId ?? null, documentIds: documentIds ?? null,
    }),

  /* One file onto the table, unattached. The id it returns is what a question then claims. */
  uploadFile:   async (file: File, note?: string) => {
    const form = new FormData()
    form.append('file', file)
    if (note) form.append('note', note)
    return api.postForm<{ id: string; fileName: string }>('/api/table/documents', form)
  },
  consult:      (id: string) => api.get<Consult>(`/api/consults/${id}`),
  consults:     () => api.get<Consult[]>('/api/consults'),
  consultThread: (id: string) => api.get<ConsultThread>(`/api/consults/${id}/messages`),
  replyToConsult: (id: string, question: string) =>
                  api.post<void>(`/api/consults/${id}/reply`, { question }),
  closeConsult: (id: string) => api.post<void>(`/api/consults/${id}/close`),
  escalate:     (id: string) => api.post<{ id: string }>(`/api/consults/${id}/escalate`),

  consultActions: (id: string) => api.get<ConsultActionItem[]>(`/api/consults/${id}/actions`),
  acceptAction: (consultId: string, actionId: string) =>
    api.post<{ id: string; dueDate: string }>(`/api/consults/${consultId}/actions/${actionId}/accept`),
  releaseAction: (consultId: string, actionId: string) =>
    api.post<void>(`/api/consults/${consultId}/actions/${actionId}/release`),
}

/* ── The help assistant (migration 041) ───────────────────────────────────── */

/**
 * The bubble in the bottom-right corner. Three calls rather than one, because the web
 * tier may not call Anthropic (SPEC 2.1): the question is recorded and enqueued, and the
 * browser comes back for the answer. `ask` returns an id; poll `answer` with it.
 */
export interface AssistantConfig {
  suggestions: string[]
  maxQuestion: number
}

export interface AssistantAnswer {
  status: 'pending' | 'done' | 'failed'
  answer: string | null
  error: string | null
}

export const assistant = {
  config: () => api.get<AssistantConfig>('/api/assistant'),

  ask: (messages: { role: 'user' | 'assistant'; content: string }[],
        screen: string | null, native: boolean) =>
    api.post<{ id: string }>('/api/assistant/ask', { messages, screen, native }),

  answer: (id: string) => api.get<AssistantAnswer>(`/api/assistant/${id}`),
}

/* ── Content, and the operator who edits it (migration 027) ───────────────── */

export interface PostSummary {
  id: string
  slug: string
  title: string
  status: 'draft' | 'published'
  publishedAt: string | null
  author: string | null
  excerpt: string | null
  tags: string[]
  updatedAt: string
  readingMinutes: number
}

export interface Post extends PostSummary {
  body: string
  metaTitle: string | null
  metaDescription: string | null
  createdAt: string
}

export interface PostDraft {
  title: string
  slug: string
  status: 'draft' | 'published'
  author: string
  excerpt: string
  body: string
  metaTitle: string
  metaDescription: string
  tags: string[]
}

export const admin = {
  me:     () => api.get<{ ok: true }>('/api/admin/me'),
  login:  (password: string) => api.post<{ ok: true }>('/api/admin/login', { password }),
  logout: () => api.post<void>('/api/admin/logout'),

  posts:  (status?: string) =>
    api.get<{ posts: PostSummary[]; counts: Record<string, number> }>(
      `/api/admin/posts${status ? `?status=${encodeURIComponent(status)}` : ''}`),

  post:   (id: string) => api.get<Post>(`/api/admin/posts/${id}`),
  create: (body: PostDraft) => api.post<{ id: string; slug: string }>('/api/admin/posts', body),
  save:   (id: string, body: PostDraft) => api.put<{ id: string; slug: string }>(`/api/admin/posts/${id}`, body),
  remove: (id: string) => api.del<void>(`/api/admin/posts/${id}`),

  slug:   (title: string, exceptId?: string) =>
    api.get<{ slug: string; taken: boolean }>(
      `/api/admin/slug?title=${encodeURIComponent(title)}${exceptId ? `&exceptId=${exceptId}` : ''}`),

  requests: (status?: string, page?: string) => {
    const q = new URLSearchParams()
    if (status) q.set('status', status)
    if (page) q.set('page', page)
    return api.get<{ items: PageRequestItem[]; counts: Record<string, number> }>(
      `/api/admin/requests${q.toString() ? `?${q}` : ''}`)
  },

  addRequest: (body: { page: string; body: string; target: string | null; shot: string | null }) =>
    api.post<{ id: string; shot: string | null }>('/api/admin/requests', { kind: 'content', ...body }),

  setRequestStatus: (id: string, status: RequestState) =>
    api.post<void>(`/api/admin/requests/${id}/status`, { status }),

  editRequest: (id: string, body: string) =>
    api.put<void>(`/api/admin/requests/${id}`, { body }),

  removeRequest: (id: string) => api.del<void>(`/api/admin/requests/${id}`),
}

/* Anonymous. Published posts only; a draft is a 404 here. */
export const content = {
  posts: () => api.get<PostSummary[]>('/api/content/posts'),
  post:  (slug: string) => api.get<Post>(`/api/content/posts/${encodeURIComponent(slug)}`),
}

/* ── Page requests (migration 028) ────────────────────────────────────────── */

export type RequestState = 'pending' | 'doing' | 'done' | 'skip'

export interface PageRequestItem {
  id: string
  status: RequestState
  kind: string
  page: string
  body: string
  target: string | null
  shot: string | null
  createdAt: string
  updatedAt: string
  selector: string | null
}

export const REQUEST_LABEL: Record<RequestState, string> = {
  pending: 'Pending',
  doing: 'In progress',
  done: 'Applied',
  skip: 'Declined',
}
